import React from 'react';
import Select from 'react-select';
import ApplyRounding from '../../../../../utils/math/ApplyRounding';
import {Typography} from '@material-ui/core';
import {
    requestApproxStrikesPerExpirationDate
} from '../../../../../stores/websocketStore/utils/outgoingMessages/optionexpirations/approxStrikesPerExpirationDate';
import {
    requestOptionExpirationDates
} from '../../../../../stores/websocketStore/utils/outgoingMessages/optionexpirations/requestOptionExpirationDates';
import {useWebsocketStore} from "../../../../../stores/websocketStore/websocketProvider";
import {filter, map} from "rxjs/operators";

// Parses Java Date.toString() format: "Thu Jan 14 00:00:00 EST 2027"
// new Date(str) is unreliable for this format (timezone abbreviations like EST/EDT fail in many browsers)
const MONTH_INDEX = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
function parseDateStr(dateStr) {
    const arr = dateStr.trim().split(/\s+/);
    if (arr.length < 6) return null;
    const month = MONTH_INDEX[arr[1]];
    if (month === undefined) return null;
    const day = parseInt(arr[2], 10);
    const year = parseInt(arr[5], 10);
    if (isNaN(day) || isNaN(year)) return null;
    return new Date(year, month, day);
}

function isFutureOrToday(dateStr, excludeToday) {
    const d = parseDateStr(dateStr);
    if (!d) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return excludeToday ? d > today : d >= today;
}

function formatExpirationLabel(dateStr) {
    const d = parseDateStr(dateStr);
    if (!d) return dateStr;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dte = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    const arr = dateStr.trim().split(/\s+/);
    if (arr.length < 6) return dateStr;
    const [dayName, monthName, day, timeStr, tz, year] = arr;
    const isMidnight = timeStr === '00:00:00';
    if (isMidnight) {
        return `${dayName} ${monthName} ${day}, ${year} (${dte}d)`;
    }
    const [hh, mm] = timeStr.split(':');
    return `${dayName} ${monthName} ${day} ${hh}:${mm} ${tz} ${year} (${dte}d)`;
}

const customStyles = {
    option: (provided, state) => ({
        ...provided,
        fontSize: 10,
        color: state.isSelected ? 'red' : 'blue',
    }),
    control: () => ({
        width: 125,
        fontSize: 10,
    }),
    singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.5 : 1;
        const transition = 'opacity 300ms';
        return {...provided, opacity, transition};
    },
};

export default function SelectExpirationDates(props) {
    const {approxStrikesPerExpirationDateSubject, optionExpirationDatesSubject} = useWebsocketStore();
    return <SelectExpirationDatesComponent strikesSubject={approxStrikesPerExpirationDateSubject} dateSubject={optionExpirationDatesSubject} {...props}/>
}

class SelectExpirationDatesComponent extends React.Component {
    constructor(props) {
        super(props);
        this.key = 'optionExpirationDate' + new Date().getTime();

        this.datesObservable = props.dateSubject
            .pipe(filter((message) => message.key === this.key))
            .pipe(map((msg) => msg.dates));

        if (props.showStrikesMessage) {
            this.strikesObservable = props.strikesSubject
                .pipe(filter((message) => message.key === this.key))
                .pipe(map((msg) => msg));
        }
        this.dataSource = props.dataSource;
        this.brokerage = props.brokerage;
        this.symbol = props.symbol;
        this.state = {
            dates: [],
            hasRecievedDates: false,
        };
        this.expirationDates = props.expirationDates;
        this.setExpirationDates = props.setExpirationDates;
        this.expirationsAdded = 0;
        this.setWeeks = this.setWeeks.bind(this);
        this.chosenDates = [];
        this.alreadyRequested = false;
        this.notExpiringToday = props.notExpiringToday;
    }

    componentDidMount() {
        this.datesSubscription = this.datesObservable.subscribe((value) => {
            this.setState({dates: []});
            let values = [];
            this.expirationDates = [];
            this.chosenDates = [];
            // Filter past dates (and today if notExpiringToday), then sort chronologically
            const filtered = value.filter((element) =>
                isFutureOrToday(element, this.notExpiringToday)
            );
            filtered.sort((a, b) => {
                const da = parseDateStr(a);
                const db = parseDateStr(b);
                if (!da || !db) return 0;
                return da - db;
            });
            let index = 0;
            filtered.forEach((element) => {
                values.push({value: element, label: formatExpirationLabel(element)});
                if (index++ < this.props.startingIndex) {
                    return;
                }
                if (this.expirationsAdded < this.props.numOfDefaultSelected) {
                    this.expirationsAdded++;
                    this.expirationDates.push(element);
                    this.hasRecievedDates = true;
                    if (this.expirationsAdded === this.props.numOfDefaultSelected) {
                        this.setExpirationDates(this.expirationDates);
                    }
                    this.chosenDates.push({
                        value: element,
                        label: formatExpirationLabel(element),
                    });
                }
            });
            this.setState({hasRecievedDates: true});
            this.setState({dates: values});
        });
        if (this.props.showStrikesMessage) {
            this.strikesSubscription = this.strikesObservable.subscribe((msg) => {
                let strikes = msg['approxStrikes'];
                if (strikes !== undefined) {
                    strikes = parseInt(strikes);
                    this.props.setHighBarrier(ApplyRounding(msg.highBarrier, 2));
                    this.props.setLowBarrier(ApplyRounding(msg.lowBarrier, 2));
                }
            });
        }
        if (!this.alreadyRequested) {
            requestOptionExpirationDates(
                this.key,
                this.symbol,
                this.dataSource,
                this.notExpiringToday,
                this.brokerage,
                this.props.accountNumber
            );
            this.alreadyRequested = true;
        }
    }

    componentWillUnmount() {
        this.datesSubscription.unsubscribe();
        if (this.props.showStrikesMessage) {
            this.strikesSubscription.unsubscribe();
        }
    }

    setWeeks(dates) {
        if (dates === undefined || dates === null) {
            this.setExpirationDates([]);
            this.chosenDates = [];
            return;
        }
        let dateValues = [];
        this.chosenDates = dates;
        if (Array.isArray(dates)) {
            dates.forEach((date) => {
                dateValues.push(date.value);
            });
        } else {
            dateValues.push(dates.value);
        }
        this.setExpirationDates(dateValues);
        this.props.setOptionChains({});
        requestApproxStrikesPerExpirationDate(
            this.key,
            this.symbol,
            this.dataSource,
            dateValues,
            this.brokerage,
        );
    }

    render() {
        if (
            this.symbol !== this.props.symbol ||
            this.dataSource !== this.props.dataSource ||
            this.notExpiringToday !== this.props.notExpiringToday ||
            this.brokerage !== this.props.brokerage
        ) {
            this.symbol = this.props.symbol;
            this.dataSource = this.props.dataSource;
            this.notExpiringToday = this.props.notExpiringToday;
            this.brokerage = this.props.brokerage;
            this.expirationsAdded = 0;
            this.chosenDates = [];
            this.setState({dates: [], hasRecievedDates: false});
            this.setExpirationDates([]);
            this.props.setOptionChains({});
            requestOptionExpirationDates(
                this.key,
                this.symbol,
                this.dataSource,
                this.notExpiringToday,
                this.props.brokerage,
                this.props.accountNumber
            );
        }
        return !this.props.hideSelect && this.state.hasRecievedDates ? (
            <div>
                <Typography style={{fontSize: 12}}>
                    Available Expiration Dates
                </Typography>
                <Select
                    styles={customStyles}
                    closeMenuOnSelect={false}
                    isMulti={this.props.isMulti}
                    options={this.state.dates}
                    value={this.chosenDates}
                    onChange={this.setWeeks}
                />
            </div>
        ) : (
            <div></div>
        );
    }
}
